import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import { progressPercent } from '../../../../components/progressPercent';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { useListSurface } from '../../../../components/table/listSurface';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { ReceiveDeliveryModal } from '../components/ReceiveDeliveryModal';
import { goodsReceiptsApi } from '../goodsReceiptApi';
import type { GoodsReceiptListItem } from '../goodsReceiptTypes';
import { GOODS_RECEIPT_STATUS } from '../goodsReceiptTypes';

/**
 * The goods-receipt register — supplier deliveries, and what each one turned into.
 *
 * <b>The anchor is the NUMBER AND the supplier, joined.</b> The source anchors on the supplier
 * name alone, and two deliveries from one supplier read identically under that — the number is
 * the row's identity, so it leads. The supplier was a column of its own until 01 Oct 2026 and is
 * now merged into the anchor on the product owner's call; see `buildColumns` for what that
 * costs and why the Supplier column had to go rather than sit beside it.
 *
 * <b>TWO state columns, deliberately.</b> The delivery's own stage as plain text, and the row's
 * live-or-deleted marker as the `status: true` pill the shell hoists to the end. A deleted
 * receipt and a cancelled one are different facts, and a reader must not have to guess which the
 * column is reporting.
 */
export function GoodsReceiptsIndexPage() {
  const navigate = useNavigate();

  /*
    The anchor merges the supplier in, so the gate that used to live on the Supplier COLUMN now
    lives in the cell. Resolved here and memoised on the one value it depends on — rebuilding
    the array every render would hand `<IndexPage>` a new `columns` identity each time.
  */
  const canReadSuppliers = usePermission('suppliers.read');
  const columns = useMemo(() => buildColumns(canReadSuppliers), [canReadSuppliers]);

  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    receipt: GoodsReceiptListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    A DELIVERY IS ALWAYS AGAINST AN ORDER, so one that can still take stock has to exist before
    the wizard can be submitted at all — its first step is the order picker. Seeded TRUE and left
    true on a failed feed: flashing "approve an order first" on every load of a working module is
    worse than a banner arriving a moment late, and a request that did not come back is not
    evidence that there is nothing on order.
  */
  const [hasReceivableOrder, setHasReceivableOrder] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    goodsReceiptsApi
      .receivableOrders(controller.signal)
      .then((orders) => {
        if (controller.signal.aborted) return;
        setHasReceivableOrder(orders.length > 0);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const fetchReceipts = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      goodsReceiptsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          source: query.facets.source?.join(','),
          supplier: query.facets.supplier?.join(','),
          facility: query.facets.facility?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (receipt: GoodsReceiptListItem): MenuItem[] => [
      {
        key: `restore-${receipt.id}`,
        label: 'Restore',
        permission: 'goods-receipts.delete',
        disabledReason: receipt.isActive ? 'This receipt is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', receipt }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await goodsReceiptsApi.deactivate(confirming.receipt.id);
      else await goodsReceiptsApi.activate(confirming.receipt.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<GoodsReceiptListItem>
        module="goods-receipts"
        breadcrumb={['Logistics', 'Inventory', 'Goods Receipts']}
        columns={columns}
        /*
          Filed by STAGE rather than the default status axis: the question a reader opens this
          register with is what is still waiting to be inspected, not whether a row is deleted.
          They repoint it from the rail's cog if they file differently.
        */
        folderNavFacet="stage"
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'stage', label: 'Stage' },

          /*
            BOTH GATED on the module that owns the value, because both are more privileged than
            the row carrying them. The supplier registry and the store list each have their own
            page, and this module's implied reads are scoped to `create` — so a read-only reader
            genuinely lacks them and the gate hides something real.
          */
          { key: 'source', label: 'Source' },
          { key: 'supplier', label: 'Supplier', permission: 'suppliers.read' },
          { key: 'facility', label: 'Store', permission: 'storage-facilities.read' },
        ]}
        prerequisites={[
          {
            label: 'an approved purchase order with something outstanding',
            met: hasReceivableOrder,
            to: '/procurement/purchasing/purchase-orders',
            permission: 'purchase-orders.read',
          },
        ]}
        /*
          Every type this module's workflow WRITES, children included — a module whose list names
          only the parent hides every write to every child, and the failure is silent in the worst
          way: the row IS in `AuditLogs` and only the module's own history is blind.
        */
        auditEntityTypes={['GoodsReceipt', 'GoodsReceiptItem', 'GoodsReceiptItemSerial']}
        rowKey={(receipt) => receipt.id}
        fetch={fetchReceipts}
        reloadKey={reloadKey}
        searchPlaceholder="Search receipt, order, supplier or invoice"
        emptyMessage="No delivery has been recorded yet."
        onCreate={() => setCreating(true)}
        createPermission="goods-receipts.create"
        onRowOpen={(receipt) => navigate(`/inventory/goods-receipts/${receipt.id}`)}
        openPermission="goods-receipts.read"
        rowActions={rowActions}
        /*
          THE RING IS HOW FAR THROUGH THE OFFICIAL INSPECTION A DELIVERY IS, drawn before the
          kebab on a list row. A cancelled receipt and one with no lines draw NOTHING rather
          than an empty circle reading 0% — they are outside the question rather than at the
          start of it. The table states the same percentage in its `Inspected` column, off this
          same derivation.
        */
        rowProgress={(receipt) => {
          const progress = inspectionOf(receipt);

          return progress
            ? { value: progress.done, total: progress.total, label: 'lines inspected' }
            : null;
        }}
        onDelete={(receipt) => setConfirming({ kind: 'deactivate', receipt })}
        deletePermission="goods-receipts.delete"
        deleteLabel={() => 'Delete'}
        /*
          Disabled for STATE, and the reason names the other action. The server refuses it too —
          the rule lives there — but meeting it as a refusal after a password prompt is a worse
          way to learn that deleting is not cancelling.
        */
        deleteDisabledReason={(receipt) =>
          !receipt.isActive
            ? 'This receipt is already deleted.'
            : receipt.status === GOODS_RECEIPT_STATUS.received
              ? 'This delivery has been registered and its stock exists. Correct it with a stock adjustment — deleting is for a receipt keyed against the wrong order.'
              : null
        }
        otherInfo={(receipt) => receipt}
      />

      {creating ? (
        <ReceiveDeliveryModal
          open
          onClose={() => setCreating(false)}
          onSaved={(receipt) => {
            setCreating(false);
            navigate(`/inventory/goods-receipts/${receipt.id}`);
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete goods receipt' : 'Restore goods receipt'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`goods-receipt-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `Receipt ${confirming.receipt.receiptNumber} stops appearing in the register.`
                  : `Receipt ${confirming.receipt.receiptNumber} appears in the register again.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  <b>Deleting is not cancelling.</b> It corrects the register — a delivery keyed
                  against the wrong order. If the delivery was abandoned, cancel it with a reason
                  so the record says what happened.
                </p>
              ) : null}

              <p>
                No stock moves either way. Nothing on an uninspected delivery has become stock
                yet, and a registered one cannot be deleted at all.
              </p>

              <p>Nothing is erased. A deleted receipt can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/**
 * The register's columns.
 *
 * <b>A FUNCTION rather than a const, because the anchor now carries the SUPPLIER</b> and the
 * supplier is gated on `suppliers.read`. A merged anchor built unconditionally would print that
 * name for a reader the gate withholds it from — the column's permission moved into the cell
 * rather than being dropped, which is the half a merge is most likely to lose.
 */
/**
 * The anchor, which carries the ORDER NUMBER on the LIST surface and not in the table.
 *
 * Product call, 05 Oct 2026, in the reader's own words: *in the GR card /table list, include the
 * PO number. in the card list, concat the PO after GR no.*
 *
 * <b>THE TABLE ALREADY HAD IT, as the `Order` column</b> — so concatenating it into the anchor
 * there would print one number twice on one row, which is the merge-never-duplicate rule this
 * very anchor was built under. A LIST row has no columns to carry it, which is the whole reason
 * the counterparty was merged in on 01 Oct, and the order number is the same case one value
 * along: it ends up in the title or it is not on the card at all.
 *
 * <b>So the two views surface the same FIELD in the shape each has</b>, which is what §3 asks
 * for — it forbids a field appearing in one view and missing from the other, not one rendered as
 * a column and the other as part of a title. `<RowPreviewModal>` is outside the list surface, so
 * it shows the table form and its own `Order` row, with no duplication either.
 *
 * It reads `ListSurfaceContext`, the mechanism `<AvatarCell>` already uses to drop its avatar on
 * the list — a cell asking which surface it is on rather than every page passing a flag down.
 */
function ReceiptAnchor({
  receipt,
  withCounterparty,
}: {
  receipt: GoodsReceiptListItem;
  withCounterparty: boolean;
}) {
  const onList = useListSurface();

  // A donation and a transfer were never ordered, so there is nothing to join.
  const code =
    onList && receipt.orderNumber
      ? `${receipt.receiptNumber} — ${receipt.orderNumber}`
      : receipt.receiptNumber;

  return withCounterparty ? (
    <TextCell code={code} value={receipt.counterparty} clamp />
  ) : (
    <TextCell value={code} mono />
  );
}

/**
 * How far through the OFFICIAL inspection a delivery is — the one derivation the ring on a list
 * row and the `Inspected` column in the table both read.
 *
 * Product call, 05 Oct 2026, in the reader's own words: *add also progress ring, it should
 * indicate % of items delivered and officially inspected.*
 *
 * <b>EVERY LINE ON A RECEIPT IS DELIVERED BY CONSTRUCTION</b> — a line exists because something
 * arrived — so inspection is the only half of that sentence left to be a fraction of.
 *
 * <b>LINES, never a quantity summed across units</b>, which is the rule the purchase request's
 * own ring states: a delivery's lines are measured in their own units, so a ring over summed
 * quantity adds boxes to laptops.
 *
 * <b>NULL IS NOT 0%.</b> A cancelled receipt was abandoned before anybody inspected it and a
 * receipt with no lines has nothing to be a fraction of; neither is *zero per cent inspected*,
 * they are outside the question, and an empty ring on them would claim work is pending that
 * nobody can do. Written twice the two surfaces would eventually round differently, which is
 * what `progressPercent` exists to prevent.
 */
function inspectionOf(receipt: GoodsReceiptListItem): { done: number; total: number } | null {
  if (receipt.status === GOODS_RECEIPT_STATUS.cancelled) return null;
  if (receipt.lineCount <= 0) return null;

  return { done: receipt.inspectedLineCount, total: receipt.lineCount };
}

function buildColumns(canReadSuppliers: boolean): ColumnDef<GoodsReceiptListItem>[] {
  return [
  {
    key: 'receiptNumber',
    header: 'Receipt',

    /*
      THE ANCHOR CARRIES THE SUPPLIER TOO — product call, 01 Oct 2026, in the reader's own
      words: *in the GR, card list, concat the Supplier as title*.

      A register of `GR-26-00002` rows says which delivery without saying whose, so the list
      view's title named a number and the reader had to read down to the summary line to find
      the supplier. This is the two-value anchor the PR and PO registers already take — ONE
      line, ONE header, ONE width, joined by the ` — ` this product joins a qualifier to a name
      with, with the number kept in the mono face by `code` while the name beside it stays prose.

      <b>MERGE, NEVER DUPLICATE: the Supplier column is GONE.</b> Keeping it would print the
      name twice on one row of the table view. What that costs is sorting by supplier — stated
      rather than discovered — and the rail's supplier facet is the better control for the
      question that sort answered. The name is still searched, which the placeholder says.

      <b>IT IS THE COUNTERPARTY RATHER THAN THE SUPPLIER since 02 Oct 2026</b>, because two of
      the three sources have none: a donation names a DONOR and a transfer names the STORE it
      came from. The server derives which, from one definition, so this cell and the profile
      cannot disagree about who a delivery came from.
    */
    width: canReadSuppliers ? 280 : 150,
    sortable: true,
    render: (receipt) => <ReceiptAnchor receipt={receipt} withCounterparty={canReadSuppliers} />,
  },
  {
    key: 'orderNumber',
    header: 'Order',
    width: 150,
    sortable: true,

    // An em dash on a donation and a transfer, which were never ordered — a fact about the
    // delivery rather than a missing value.
    render: (receipt) => <TextCell value={receipt.orderNumber ?? '—'} mono />,
  },
  {
    key: 'deliveredOn',
    header: 'Delivered',
    width: 140,
    sortable: true,
    summary: true,
    render: (receipt) => <TextCell value={formatDateOnly(receipt.deliveredOn)} mono />,
  },
  {
    key: 'items',
    header: 'Items',
    width: 240,
    summary: true,
    render: (receipt) => <TextCell value={receipt.items ?? '—'} />,
  },
  {
    key: 'destination',
    header: 'Destination',
    width: 180,
    permission: 'storage-facilities.read',
    render: (receipt) => <TextCell value={receipt.destination ?? '—'} />,
  },
  {
    key: 'lineCount',
    header: 'Lines',
    width: 90,
    align: 'right',
    render: (receipt) => <TextCell value={String(receipt.lineCount)} mono numeric />,
  },
  {
    key: 'totalReceived',
    header: 'Received',
    width: 110,
    align: 'right',
    render: (receipt) => <TextCell value={String(receipt.totalReceived)} mono numeric />,
  },
  {
    /*
      AN EM DASH WHILE ANY LINE IS UNINSPECTED, never a running partial. A zero would claim the
      delivery was rejected outright, and a partial sum reads as a finished figure — the same
      null-is-not-zero distinction the line's own accepted quantity draws.
    */
    key: 'totalAccepted',
    header: 'Accepted',
    width: 110,
    align: 'right',
    render: (receipt) => (
      <TextCell
        value={receipt.totalAccepted == null ? '—' : String(receipt.totalAccepted)}
        mono
        numeric
      />
    ),
  },
  {
    /*
      THE TABLE OWES THE SAME PERCENTAGE THE RING STATES, off the SAME derivation — a list row
      draws the wedge and a table row has a column for it, and written twice the two would
      eventually round differently with both screens internally consistent. `progressPercent`
      is the one definition, and `inspectionOf` is the one source of the fraction.

      An em dash where there is nothing to be a fraction of — a cancelled receipt, or one with
      no lines — because 0% would claim an inspection is pending that nobody can perform.
    */
    key: 'inspected',
    header: 'Inspected',
    width: 100,
    align: 'right',
    render: (receipt) => {
      const progress = inspectionOf(receipt);

      return (
        <TextCell
          value={progress ? `${progressPercent(progress.done, progress.total)}%` : '—'}
          mono
          numeric
        />
      );
    },
  },
  {
    key: 'invoiceNumber',
    header: 'Invoice',
    width: 150,
    render: (receipt) => <TextCell value={receipt.invoiceNumber ?? '—'} mono />,
  },
  {
    /*
      PLAIN TEXT beside the status pill, never a second badge. Two badges side by side make a
      reader read both to learn one thing — the disciplinary panel's own split. `stage: true` is
      what keeps it adjacent to the pill in BOTH views.
    */
    key: 'stage',
    header: 'Stage',
    width: 150,
    stage: true,
    render: (receipt) => <StatusText tone="inactive">{receipt.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (receipt) => (
      <StatusText tone={receipt.isActive ? 'active' : 'inactive'}>
        {receipt.isActive ? 'Active' : 'Deleted'}
      </StatusText>
    ),
  },
  ];
}
